import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useCallback, useEffect, useMemo, useRef, memo } from "react";
import { Card } from "@volca/ui/components/ui/card";
import { cn } from "@volca/ui/lib/utils";
import { REFERRAL_COLUMN } from "@/referrals/constants";
import type { IReferral, ReferralColumn } from "@/referrals/referral";
import {
  ClosedReferralCardContent,
  ClosedReferralCardSkeleton,
} from "./content/closed-referral-card-content";
import {
  InProgressReferralCardContent,
  InProgressReferralCardSkeleton,
} from "./content/in-progress-referral-card-content";
import {
  NewReferralCardContent,
  NewReferralCardSkeleton,
} from "./content/new-referral-card-content";
import {
  ScheduledReferralCardContent,
  ScheduledReferralCardSkeleton,
} from "./content/scheduled-referral-card-content";

// Clean interface without loading state
interface ReferralCardProps {
  referral: IReferral;
  column: ReferralColumn;
  isDragging?: boolean;
  isSelected?: boolean;
  onClick: () => void;
  onNextColumn: (column: ReferralColumn) => void;
  onPayout: () => void;
  onLost?: () => void;
  onMoveToPaid?: () => void;
  onDelete?: () => void;
}

interface ReferralCardSkeletonProps {
  column: ReferralColumn;
  className?: string;
}

export const ReferralCardSkeleton = ({ column }: ReferralCardSkeletonProps) => {
  const SkeletonContent = useMemo(() => {
    if (column === "new") {
      return <NewReferralCardSkeleton />;
    }
    if (column === "inProgress") {
      return <InProgressReferralCardSkeleton />;
    }
    if (column === "scheduled") {
      return <ScheduledReferralCardSkeleton />;
    }
    if (column === "closed") {
      return <ClosedReferralCardSkeleton />;
    }
    return null;
  }, [column]);

  return (
    <Card className={cn("w-full animate-pulse gap-0 py-0")}>
      {SkeletonContent}
    </Card>
  );
};

const ReferralCard = memo(
  ({
    referral,
    column,
    isDragging,
    isSelected,
    onClick,
    onNextColumn,
    onPayout,
    onLost,
    onMoveToPaid,
    onDelete,
  }: ReferralCardProps) => {
    const { listeners, setNodeRef, transform } = useDraggable({
      id: referral.id,
      data: { column },
    });

    const cardRef = useRef<HTMLDivElement | null>(null);
    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        cardRef.current = node;
        setNodeRef(node);
      },
      [setNodeRef],
    );
    useEffect(() => {
      if (!isSelected) return;
      const timer = setTimeout(
        () =>
          cardRef.current?.scrollIntoView?.({
            behavior: "smooth",
            block: "nearest",
            inline: "nearest",
          }),
        320,
      );
      return () => clearTimeout(timer);
    }, [isSelected]);

    const style = {
      transform: CSS.Translate.toString(transform),
      transition: transform ? undefined : "transform 200ms ease",
    };

    const Content = useMemo(() => {
      if (column === "new") {
        return (
          <NewReferralCardContent
            referral={referral}
            onAccept={() => onNextColumn(REFERRAL_COLUMN.inProgress)}
            onLost={onLost}
            onMoveToPaid={onMoveToPaid}
            onDelete={onDelete}
          />
        );
      }
      if (column === "inProgress") {
        return (
          <InProgressReferralCardContent
            referral={referral}
            onLost={onLost}
            onMoveToPaid={onMoveToPaid}
            onDelete={onDelete}
          />
        );
      }
      if (column === "scheduled") {
        return (
          <ScheduledReferralCardContent
            referral={referral}
            onLost={onLost}
            onMoveToPaid={onMoveToPaid}
            onDelete={onDelete}
          />
        );
      }
      if (column === "closed") {
        return (
          <ClosedReferralCardContent
            referral={referral}
            onPayout={onPayout}
            onLost={onLost}
            onMoveToPaid={onMoveToPaid}
            onDelete={onDelete}
          />
        );
      }
      return null;
    }, [
      column,
      referral,
      onLost,
      onMoveToPaid,
      onDelete,
      onNextColumn,
      onPayout,
    ]);

    return (
      <Card
        ref={setRefs}
        style={style}
        className={cn(
          "w-full gap-0 py-0 active:cursor-grabbing",
          { "opacity-0": !!isDragging },
          { "border-selected-line ring-1 ring-selected-line": !!isSelected },
        )}
        onClick={() => {
          if (!transform) {
            onClick?.();
          }
        }}
        data-cy="referral-card"
        data-selected={isSelected || undefined}
        {...listeners}
      >
        {Content}
      </Card>
    );
  },
);

export default ReferralCard;
